import {
	ChangeDetectionStrategy,
	Component,
	Inject,
	OnInit,
	signal,
} from '@angular/core';
import { NgForm } from '@angular/forms';
import { BaseComponent } from '@core/base.component';
import {
	createId,
	getFileExtension,
	FILE_IMAGE_TYPES,
	createBusinessLogoFile,
} from '@core/util/file';
import { Address } from '@models/address';
import {
	BusinessAddress,
	BusinessUser,
	COMMISSIONER_SELECTION_TYPE,
	DefaultBusinessAddress,
	SIGNING_LOCATION_TYPE,
	SIGNING_REGION_TYPE,
	WITNESS_SELECTION_TYPE,
} from '@models/businesses';
import { BusinessAddressesService } from '@repos/businesses/business-addresses.service';
import { SegmentedButtonOption } from 'src/app/ui/button-segmented/button-segmented.component';
import { DRAWER_DATA } from 'src/app/ui/drawer/drawer-data.token';
import { DrawerService } from 'src/app/ui/drawer/drawer.service';
import { FileUploadDispatch } from '@state/file-upload/fileUpload.dispatch';
import { FileUpload } from '@state/file-upload/fileUpload.state';
import { FileUploadType } from '@state/file-upload/fileUpload.model';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { UserProfileState } from '@state/user-profile';
import { SnackbarService } from 'src/app/shared/services/ui/snackbar.service';
import { UploadService } from 'src/app/shared/services/file/upload.service';
import { Logger } from '@core/logger';
import { TestHooks } from './address-dialog.hooks';

const CommissionerRadioGroupItems: SegmentedButtonOption[] = [
	{ label: 'Lawyer', id: COMMISSIONER_SELECTION_TYPE.LAWYER },
	{ label: 'Clerk', id: COMMISSIONER_SELECTION_TYPE.CLERK },
	{ label: 'Other', id: COMMISSIONER_SELECTION_TYPE.OTHER },
];

const WitnessRadioGroupItems: SegmentedButtonOption[] = [
	{ label: 'Lawyer', id: WITNESS_SELECTION_TYPE.LAWYER },
	{ label: 'Clerk', id: WITNESS_SELECTION_TYPE.CLERK },
	{ label: 'Other', id: WITNESS_SELECTION_TYPE.OTHER },
];

export const LocationTypeDropdownOptions: {
	label: string;
	id: SIGNING_LOCATION_TYPE;
}[] = [
	{ label: 'City', id: SIGNING_LOCATION_TYPE.CITY },
	{ label: 'Municipality', id: SIGNING_LOCATION_TYPE.MUNICIPALITY },
	{ label: 'Town', id: SIGNING_LOCATION_TYPE.TOWN },
	{ label: 'Township', id: SIGNING_LOCATION_TYPE.TOWNSHIP },
	{ label: 'Village', id: SIGNING_LOCATION_TYPE.VILLAGE },
];

export const RegionTypeDropdownOptions: {
	label: string;
	id: SIGNING_REGION_TYPE;
}[] = [
	{ label: 'County', id: SIGNING_REGION_TYPE.COUNTY },
	{ label: 'District', id: SIGNING_REGION_TYPE.DISTRICT },
	{ label: 'Province', id: SIGNING_REGION_TYPE.PROVINCE },
	{
		label: 'Regional Municipality',
		id: SIGNING_REGION_TYPE.REGIONAL_MUNICIPALITY,
	},
];

interface VM {
	businessAddress: BusinessAddress;
	busy: boolean;
	submitted: boolean;
	primaryOrig: boolean;
}

const defaultVM: VM = {
	businessAddress: DefaultBusinessAddress,
	busy: true,
	submitted: false,
	primaryOrig: DefaultBusinessAddress.primary,
};

@Component({
	selector: 'app-address-dialog',
	templateUrl: './address-dialog.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class AddressDialogComponent
	extends BaseComponent<VM>
	implements OnInit
{
	testHooks = TestHooks;
	public COMMISSIONER_SELECTION_TYPE = COMMISSIONER_SELECTION_TYPE;
	public WITNESS_SELECTION_TYPE = WITNESS_SELECTION_TYPE;
	public commissionerRadioGroupItems = CommissionerRadioGroupItems;
	public witnessRadioGroupItems = WitnessRadioGroupItems;
	public locationTypeDropdownOptions = LocationTypeDropdownOptions;
	public regionTypeDropdownOptions = RegionTypeDropdownOptions;
	public readonly FILE_IMAGE_TYPES = FILE_IMAGE_TYPES;

	@SelectSnapshot(UserProfileState.getCurrentBusinessId) businessId: string;

	public docId_ = signal('');
	private currentLogoPath_ = signal<string | null>(null);

	constructor(
		private businessAddressesService: BusinessAddressesService,
		@Inject(DRAWER_DATA) private data: { address: BusinessAddress },
		private drawerService: DrawerService,
		private fileUploadDispatch: FileUploadDispatch,
		private uploadService: UploadService,
		private snackbarService: SnackbarService,
	) {
		super(defaultVM);
	}

	ngOnInit(): void {
		const defaultBusinessAddress = structuredClone(DefaultBusinessAddress);
		const addressData = this.data?.address
			? structuredClone(this.data?.address)
			: {};

		const businessAddress = {
			...defaultBusinessAddress,
			...addressData,
		};
		this.VM = {
			businessAddress: businessAddress,
			busy: false,
			primaryOrig: businessAddress.primary,
		};
	}

	onAddressUpdate(address: Address): void {
		this.VM = {
			businessAddress: {
				...this.VM.businessAddress,
				city: address.city || '',
				country: address.country || '',
				poBox: address.poBox || '',
				postcode: address.postcode || '',
				province: address.province || '',
				street: address.street || '',
				street2: address.street2 || '',
				unit: address.unit || '',
			},
			busy: false,
		};
	}

	setCommissionerUser(user: BusinessUser) {
		this.VM = {
			businessAddress: {
				...this.VM.businessAddress,
				otherCommissionerId: user.id,
			},
		};
	}

	setWitnessUser(user: BusinessUser) {
		this.VM = {
			businessAddress: {
				...this.VM.businessAddress,
				otherWitnessId: user.id,
			},
		};
	}

	get hasSigningLocationData(): boolean {
		const addr = this.VM.businessAddress;
		return !!(
			addr.signingLocationType ||
			addr.signingRegionType ||
			addr.signingLocation ||
			addr.signingRegion
		);
	}

	saveAddress(form: NgForm): void {
		this.VM = {
			submitted: true,
		};

		if (!form.valid) {
			form.control.markAllAsTouched();
			return;
		}

		this.VM = {
			busy: true,
		};
		if (this.VM.businessAddress.id) {
			// update an existing address
			this.businessAddressesService
				.update(this.VM.businessAddress.id, this.VM.businessAddress)
				.subscribe(() => {
					this.VM = {
						busy: false,
					};
					this.drawerService.closeDrawer();
				});
		} else {
			// create a new address
			this.businessAddressesService
				.create({
					...this.VM.businessAddress,
					id: createId(),
				})
				.subscribe(() => {
					this.VM = {
						busy: false,
					};
					this.drawerService.closeDrawer();
				});
		}
	}

	fileSelected(file: File): void {
		if (!file) return;

		this.docId_.set(createId());

		const addressId = this.VM.businessAddress.id || createId();
		const extension = getFileExtension(file.name);
		let filePath = `${this.businessId}/address/${addressId}/${this.docId_()}`;

		if (extension) {
			filePath = `${filePath}.${extension}`;
		}

		this.currentLogoPath_.set(filePath);

		this.fileUploadDispatch.UploadFile({
			uploadType: FileUploadType.BUSINESS,
			objectId: this.businessId,
			docId: this.docId_(),
			name: `office_logo`,
			path: filePath,
			file,
		});
	}

	onSuccess($event: FileUpload): void {
		if ($event && $event.publicUrl) {
			const logoFile = createBusinessLogoFile(
				$event.publicUrl,
				$event.name,
				this.businessId,
			);

			this.VM = {
				businessAddress: {
					...this.VM.businessAddress,
					logoFile: logoFile,
				},
			};
		}
	}

	async resetLogo(): Promise<void> {
		try {
			const pathToDelete = this.currentLogoPath_();

			if (pathToDelete) {
				await this.uploadService.delete(pathToDelete);
			}

			this.currentLogoPath_.set(null);

			this.docId_.set(createId());

			this.VM = {
				businessAddress: {
					...this.VM.businessAddress,
					logoFile: null,
				},
			};
		} catch (error) {
			Logger.error(`Failed to delete logo file: ${error}`);
			this.snackbarService.openSnackBar({
				text: 'Failed to delete logo. Please try again.',
				type: 'error',
			});
		}
	}
}
